import React from 'react';
import { Box, Chip, CircularProgress, Paper, Typography } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Translate } from 'react-localize-redux';
import ShotTypeLabel from './ShotTypeLabel';
import { ReviewImage, isCurtained } from './imageMeta';
import { QcVisitShotSkipDto } from '../../../services/qualityCheck';

/** What the panel needs of a photograph: where its pixels are, and whether to curtain it. */
export type VisitShotImage = Pick<ReviewImage, 'encryptedFileUuid' | 'localId' | 'identifiable' | 'bytes'>;

export interface VisitShot {
    /** The eCRF image field, e.g. SKIN-41's `healthy_skin_image`. Names the shot. */
    fieldName: string;
    /** The photograph on the visit for this field, or null when it was never taken. */
    image: VisitShotImage | null;
    /**
     * SKIN-637 — the dermatologist recorded the shot as not possible, with this reason.
     * Absent or null: not skipped, so a null `image` is missing.
     */
    skipped?: QcVisitShotSkipDto | null;
}

interface VisitShotsPanelProps {
    /** The study's required visit-level shots, present or missing (the API's `requiredVisitShots`). */
    shots: VisitShot[];
    /** The visit is approved (LOCKED). Nothing here is actionable either way today. */
    readOnly?: boolean;
}

const THUMB = 160;

/**
 * SKIN-618 — the shots a visit must carry once, outside any lesion (SKIN-41's healthy-skin
 * reference).
 *
 * They are checked for presence only (David, 2026-10-06): no verdict, no categories, and
 * approval does not wait on them, so this is a statement of fact rather than a review card.
 * A present shot is shown for context; a missing one says so; a skipped one (SKIN-637) states
 * the reason the dermatologist recorded, in the study's own words, and is not a finding. The
 * curtain still holds for a photograph flagged identifiable — context is not a reason to
 * uncover a patient.
 */
export default function VisitShotsPanel({ shots, readOnly = false }: VisitShotsPanelProps) {
    if (shots.length === 0) return null;

    return (
        <Box sx={{ mb: 3 }} data-testid="visit-shots-panel" data-read-only={readOnly ? 'true' : undefined}>
            <Typography variant="subtitle1" component="h5" sx={{ mb: 0.5 }}>
                <Translate id="hospital.quality-check.review.visit-shots" />
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
                {shots.map((shot) => (
                    <Paper
                        key={shot.fieldName}
                        variant="outlined"
                        sx={{ p: 1.5, width: THUMB + 24 }}
                        data-testid="visit-shot"
                        data-field={shot.fieldName}
                        data-present={String(shot.image !== null)}
                        data-skipped={String(Boolean(shot.skipped))}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                <ShotTypeLabel fieldName={shot.fieldName} />
                            </Typography>
                            {shot.skipped ? (
                                <SkippedChip skipped={shot.skipped} />
                            ) : shot.image ? (
                                <Chip
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                    label={<Translate id="hospital.quality-check.review.visit-shot-present" />}
                                />
                            ) : (
                                <Chip
                                    size="small"
                                    color="warning"
                                    data-testid="visit-shot-missing"
                                    label={<Translate id="hospital.quality-check.review.visit-shot-missing" />}
                                />
                            )}
                        </Box>
                        {shot.skipped?.note && (
                            <Typography variant="caption" component="p" sx={{ mb: 1 }} data-testid="visit-shot-skipped-note">
                                {shot.skipped.note}
                            </Typography>
                        )}
                        {shot.image && <ShotPixels image={shot.image} />}
                    </Paper>
                ))}
            </Box>
        </Box>
    );
}

/** "Skipped: <reason>". The reason is already in the study's language; only the prefix is translated. */
function SkippedChip({ skipped }: { skipped: QcVisitShotSkipDto }) {
    return (
        <Chip
            size="small"
            color="default"
            variant="outlined"
            data-testid="visit-shot-skipped"
            sx={{ maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25 } }}
            label={skipped.reason
                ? <><Translate id="hospital.quality-check.review.visit-shot-skipped" /> {skipped.reason}</>
                : <Translate id="hospital.quality-check.review.visit-shot-skipped-no-reason" />}
        />
    );
}

function ShotPixels({ image }: { image: VisitShotImage }) {
    const frame = { width: THUMB, height: Math.round((THUMB * 3) / 4), display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'action.hover', borderRadius: 1 };
    if (isCurtained(image)) {
        return (
            <Box sx={{ ...frame, flexDirection: 'column', textAlign: 'center', px: 1 }} data-testid="visit-shot-curtain">
                <LockOutlinedIcon color="action" fontSize="small" />
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    <Translate id="hospital.quality-check.review.identifiable-hidden" />
                </Typography>
            </Box>
        );
    }
    if (image.bytes.state === 'ready') {
        return (
            <Box
                component="img"
                src={image.bytes.blobUrl}
                alt=""
                sx={{ ...frame, objectFit: 'cover' }}
            />
        );
    }
    if (image.bytes.state === 'loading') {
        return <Box sx={frame}><CircularProgress size={20} /></Box>;
    }
    // Failed: the shot exists, which is all this panel states; the pixels are a courtesy.
    return null;
}
